/**
 * Kestrel 2026-10-21 — variant guard for the Horizon variant picker.
 *
 * snippets/variant-main-picker.liquid already hides option values that have no variant
 * combined with the other selected values. This script is the safety net: if a selection
 * would still point at a combination with no variant (deep link, restored form state,
 * dropdown, keyboard), it switches the other options to the first valid variant that has
 * the value the buyer just picked (in stock first, then sold-out), before Horizon fetches
 * the re-rendered section. So the page never shows an "Unavailable" combination and the
 * gallery follows that variant's photo. Products with one option, or with a single
 * default variant (e.g. Mageworx-built products), have no variant map and are untouched.
 */
const MAP_ATTR = 'data-kestrel-variant-map';
const cache = new Map();

/** @param {HTMLElement} picker */
function getVariants(picker) {
  const productId = picker.dataset.productId;
  if (!productId) return null;
  if (cache.has(productId)) return cache.get(productId);
  const el = document.querySelector(`script[${MAP_ATTR}="${productId}"]`);
  let variants = null;
  try {
    variants = el ? JSON.parse(el.textContent || 'null') : null;
  } catch (e) {
    variants = null;
  }
  if (!Array.isArray(variants) || !variants.length) variants = null;
  cache.set(productId, variants);
  return variants;
}

/** Option position (0-based) of an input/option element, from data-input-id="<position>-<index>". */
function positionOf(el) {
  const pos = Number.parseInt((el.dataset.inputId || '').split('-')[0], 10);
  return Number.isNaN(pos) ? -1 : pos - 1;
}

/** Current selection per option position; null = not selectable here (single-value option). */
function currentSelection(picker, count) {
  const sel = new Array(count).fill(null);
  picker.querySelectorAll('fieldset input:checked').forEach((input) => {
    const p = positionOf(input);
    if (p >= 0 && p < count) sel[p] = input.value;
  });
  picker.querySelectorAll('select').forEach((select) => {
    const opt = select.options[select.selectedIndex];
    if (!opt) return;
    const p = positionOf(opt);
    if (p >= 0 && p < count) sel[p] = opt.value;
  });
  return sel;
}

function matches(variant, sel) {
  return sel.every((value, i) => value === null || variant.options[i] === value);
}

/** First valid variant that keeps sel[anchor]; in stock first, then most other values kept. */
function fallbackFor(variants, sel, anchor) {
  const pool = variants.filter((v) => anchor < 0 || sel[anchor] === null || v.options[anchor] === sel[anchor]);
  const candidates = pool.length ? pool : variants;
  const score = (v) => (v.available ? 1000 : 0) + sel.reduce((n, value, i) => n + (value !== null && v.options[i] === value ? 1 : 0), 0);
  let best = null;
  for (const v of candidates) if (!best || score(v) > score(best)) best = v;
  return best;
}

/** Move every other option to the fallback variant's values, using Horizon's own updateSelectedOption. */
function applyVariant(picker, variant, skipPosition) {
  picker.querySelectorAll('fieldset').forEach((fieldset) => {
    const inputs = Array.from(fieldset.querySelectorAll('input'));
    if (!inputs.length) return;
    const p = positionOf(inputs[0]);
    if (p < 0 || p === skipPosition) return;
    const target = inputs.find((i) => i.value === variant.options[p]);
    if (target && !target.checked) {
      if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(target);
      else target.checked = true;
    }
  });
  picker.querySelectorAll('select').forEach((select) => {
    const first = select.options[0];
    if (!first) return;
    const p = positionOf(first);
    if (p < 0 || p === skipPosition) return;
    const value = variant.options[p];
    if (select.value === value) return;
    select.value = value;
    if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(select);
  });
}

// Capture phase: runs before VariantPicker's own (bubbling) change handler builds its request.
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement)) return;
    const variants = getVariants(picker);
    if (!variants) return;

    const count = variants[0].options.length;
    const chosen = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!chosen) return;
    const anchor = positionOf(chosen);
    if (anchor < 0) return;

    const sel = currentSelection(picker, count);
    sel[anchor] = chosen.value;
    if (variants.some((v) => matches(v, sel))) return; // valid combination, nothing to do

    const fallback = fallbackFor(variants, sel, anchor);
    if (!fallback) return;
    applyVariant(picker, fallback, anchor);
    chosen.dataset.variantId = String(fallback.id); // so Horizon writes ?variant=<fallback> to the URL
  },
  true
);

/**
 * 2026-10-29: keep ?variant= in step with the selection, immediately.
 * Horizon writes the URL after yielding to the main thread, using the data-variant-id printed on the clicked input.
 * That id belongs to the combination rendered *before* the click, so after quick successive clicks (or before the
 * section re-render lands) the URL could show a stale variant for a while or end up wrong. Here the id is looked up
 * from the variant map for the current selection, written synchronously on change, and re-asserted a few times so
 * Horizon's deferred (older) write can never leave the URL behind the buyer's choice.
 */
let latestVariantId = null;
function writeVariantParam() {
  if (latestVariantId === null) return;
  if (window.kSheathOff) return; // 2026-11-01: sheath deselected (kestrel-sheath-toggle) - the URL carries option_values + sheath=none instead
  const url = new URL(window.location.href);
  if (url.searchParams.get('variant') === String(latestVariantId)) return;
  url.searchParams.set('variant', String(latestVariantId));
  history.replaceState(history.state, '', url.toString());
}

document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement)) return;
    // Only the main product page's picker owns the URL (not cards, quick-add, featured product sections).
    if (picker.dataset.templateProductMatch !== 'true') return;
    if (target.closest('product-card') || target.closest('quick-add-dialog')) return;
    const chosen = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!chosen || (chosen.dataset && chosen.dataset.connectedProductUrl)) return; // combined listing: Horizon navigates
    const variants = getVariants(picker);
    if (!variants) return;
    const sel = currentSelection(picker, variants[0].options.length);
    const match = variants.find((v) => matches(v, sel));
    if (!match) return;
    latestVariantId = match.id;
    writeVariantParam();
    [0, 60, 250, 700, 1500].forEach((ms) => setTimeout(writeVariantParam, ms));
  },
  true
);

/** Page opened on a combination with no variant (e.g. ?option_values= deep link): switch once. */
function fixInitial(picker) {
  const variants = getVariants(picker);
  if (!variants || picker.dataset.kestrelGuardChecked) return;
  picker.dataset.kestrelGuardChecked = 'true';
  const json = picker.querySelector('script[type="application/json"]');
  if (!json || (json.textContent || '').trim() !== 'null') return;

  const count = variants[0].options.length;
  const sel = currentSelection(picker, count);
  const anchor = sel.findIndex((v) => v !== null);
  const fallback = fallbackFor(variants, sel, anchor);
  if (!fallback) return;
  const anchorInput = Array.from(picker.querySelectorAll('fieldset input')).find(
    (i) => positionOf(i) === anchor && i.value === fallback.options[anchor]
  );
  applyVariant(picker, fallback, anchor);
  if (anchorInput) {
    anchorInput.dataset.variantId = String(fallback.id);
    anchorInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function init() {
  document.querySelectorAll('variant-picker').forEach((p) => {
    if (p instanceof HTMLElement) {
      if (customElements.get('variant-picker')) fixInitial(p);
      else customElements.whenDefined('variant-picker').then(() => fixInitial(p));
    }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
